import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Divider,
  Paper,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Link
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import html2pdf from 'html2pdf.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dobanda-tabpanel-${index}`}
      aria-labelledby={`dobanda-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CalculatorDobanzi = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // State pentru dobândă simplă
  const [capitalInitial, setCapitalInitial] = useState('');
  const [rataDobanzi, setRataDobanzi] = useState('');
  const [tipRata, setTipRata] = useState('anuala'); // anuala, lunara, zilnica
  const [dataStart, setDataStart] = useState(null);
  const [dataFinal, setDataFinal] = useState(null);
  const [tipCalcul, setTipCalcul] = useState('simpla'); // simpla, compusa
  
  // State pentru dobânzi legale
  const [capitalLegal, setCapitalLegal] = useState('');
  const [dataStartLegal, setDataStartLegal] = useState(null);
  const [dataFinalLegal, setDataFinalLegal] = useState(null);
  const [rataLegala, setRataLegala] = useState('8'); // Rata BNR curentă aproximativă
  
  // State pentru rezultate
  const [rezultatSimpla, setRezultatSimpla] = useState(null);
  const [rezultatLegala, setRezultatLegala] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Calculează dobânda simplă/compusă
  useEffect(() => {
    if (capitalInitial && rataDobanzi && dataStart && dataFinal) {
      const capital = parseFloat(capitalInitial);
      const rata = parseFloat(rataDobanzi);
      const startDate = new Date(dataStart);
      const endDate = new Date(dataFinal);
      
      if (capital > 0 && rata > 0 && endDate > startDate) {
        // Calculează numărul de zile
        const diferentaMs = endDate.getTime() - startDate.getTime();
        const zile = Math.ceil(diferentaMs / (1000 * 60 * 60 * 24));
        
        let rataZilnica = rata;
        let perioada = zile;
        
        // Convertește rata în funcție de tip
        if (tipRata === 'anuala') {
          rataZilnica = rata / 365;
        } else if (tipRata === 'lunara') {
          rataZilnica = rata / 30;
        }
        
        let dobandaCalculata;
        let totalDePlata;
        
        if (tipCalcul === 'simpla') {
          // Dobândă simplă: D = C × r × t
          dobandaCalculata = capital * (rataZilnica / 100) * zile;
          totalDePlata = capital + dobandaCalculata;
        } else {
          // Dobândă compusă: A = C × (1 + r)^t
          const factorCompus = Math.pow(1 + (rataZilnica / 100), zile);
          totalDePlata = capital * factorCompus;
          dobandaCalculata = totalDePlata - capital;
        }
        
        setRezultatSimpla({
          capitalInitial: capital.toFixed(2),
          rataDobanzi: rata.toFixed(2),
          tipRata: tipRata,
          tipCalcul: tipCalcul,
          zile: zile,
          dataStart: startDate.toLocaleDateString('ro-RO'),
          dataFinal: endDate.toLocaleDateString('ro-RO'),
          dobandaCalculata: dobandaCalculata.toFixed(2),
          totalDePlata: totalDePlata.toFixed(2),
          calculDesfasurat: tipCalcul === 'simpla' 
            ? `Dobândă simplă = Capital × Rata × Timp = ${capital.toFixed(2)} × ${rataZilnica.toFixed(4)}% × ${zile} zile = ${dobandaCalculata.toFixed(2)} lei`
            : `Dobândă compusă = Capital × (1 + Rata)^Timp = ${capital.toFixed(2)} × (1 + ${rataZilnica.toFixed(4)}%)^${zile} = ${dobandaCalculata.toFixed(2)} lei`
        });
      }
    } else {
      setRezultatSimpla(null);
    }
  }, [capitalInitial, rataDobanzi, tipRata, dataStart, dataFinal, tipCalcul]);

  // Calculează dobânda legală
  useEffect(() => {
    if (capitalLegal && dataStartLegal && dataFinalLegal && rataLegala) {
      const capital = parseFloat(capitalLegal);
      const rata = parseFloat(rataLegala);
      const startDate = new Date(dataStartLegal);
      const endDate = new Date(dataFinalLegal);
      
      if (capital > 0 && rata > 0 && endDate > startDate) {
        const diferentaMs = endDate.getTime() - startDate.getTime();
        const zile = Math.ceil(diferentaMs / (1000 * 60 * 60 * 24));
        
        // Dobânda legală se calculează anual, apoi se împarte la zile
        const rataZilnica = rata / 365;
        const dobandaLegala = capital * (rataZilnica / 100) * zile;
        const totalDePlata = capital + dobandaLegala;
        
        setRezultatLegala({
          capitalLegal: capital.toFixed(2),
          rataLegala: rata.toFixed(2),
          zile: zile,
          dataStart: startDate.toLocaleDateString('ro-RO'),
          dataFinal: endDate.toLocaleDateString('ro-RO'),
          dobandaLegala: dobandaLegala.toFixed(2),
          totalDePlata: totalDePlata.toFixed(2),
          calculDesfasurat: `Dobândă legală = Capital × (Rata anuală / 365) × Zile = ${capital.toFixed(2)} × (${rata}% / 365) × ${zile} zile = ${dobandaLegala.toFixed(2)} lei`
        });
      }
    } else {
      setRezultatLegala(null);
    }
  }, [capitalLegal, dataStartLegal, dataFinalLegal, rataLegala]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const resetCalculatorSimpla = () => {
    setCapitalInitial('');
    setRataDobanzi('');
    setTipRata('anuala');
    setDataStart(null);
    setDataFinal(null);
    setTipCalcul('simpla');
    setRezultatSimpla(null);
  };

  const resetCalculatorLegala = () => {
    setCapitalLegal('');
    setDataStartLegal(null);
    setDataFinalLegal(null);
    setRataLegala('8');
    setRezultatLegala(null);
  };

  const copiazaRezultat = (rezultat, tip) => {
    if (rezultat) {
      let textDeCopiat;
      if (tip === 'simpla') {
        textDeCopiat = `Dobândă ${rezultat.tipCalcul} pentru ${rezultat.zile} zile (${rezultat.dataStart} - ${rezultat.dataFinal}): ${rezultat.dobandaCalculata} lei. Capital inițial: ${rezultat.capitalInitial} lei. Total de plată: ${rezultat.totalDePlata} lei.`;
      } else {
        textDeCopiat = `Dobândă legală pentru ${rezultat.zile} zile (${rezultat.dataStart} - ${rezultat.dataFinal}): ${rezultat.dobandaLegala} lei. Capital inițial: ${rezultat.capitalLegal} lei. Total de plată: ${rezultat.totalDePlata} lei.`;
      }
      
      navigator.clipboard.writeText(textDeCopiat).then(() => {
        setOpenSnackbar(true);
      });
    }
  };

  const exportaPDF = (rezultat, tip) => {
    if (rezultat) {
      const element = document.createElement('div');
      const titlu = tip === 'simpla' ? 'Calculator Dobânzi Generale' : 'Calculator Dobânzi Legale';
      
      element.innerHTML = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
          <h2 style="text-align: center; color: #333; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
            ${titlu}
          </h2>
          
          <div style="margin: 20px 0;">
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Capital inițial:</strong></span>
              <span><strong>${tip === 'simpla' ? rezultat.capitalInitial : rezultat.capitalLegal} lei</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Rata dobânzii:</strong></span>
              <span><strong>${tip === 'simpla' ? rezultat.rataDobanzi + '% (' + rezultat.tipRata + ')' : rezultat.rataLegala + '% (anuală)'}</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Perioada:</strong></span>
              <span><strong>${rezultat.zile} zile (${rezultat.dataStart} - ${rezultat.dataFinal})</strong></span>
            </div>
            
            ${tip === 'simpla' ? `
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Tip calcul:</strong></span>
              <span><strong>${rezultat.tipCalcul === 'simpla' ? 'Dobândă simplă' : 'Dobândă compusă'}</strong></span>
            </div>
            ` : ''}
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee;">
              <span><strong>Dobândă calculată:</strong></span>
              <span style="color: #dc3545;"><strong>${tip === 'simpla' ? rezultat.dobandaCalculata : rezultat.dobandaLegala} lei</strong></span>
            </div>
            
            <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 2px solid #ccc;">
              <span><strong>Total de plată:</strong></span>
              <span style="color: #28a745; font-size: 18px;"><strong>${rezultat.totalDePlata} lei</strong></span>
            </div>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 5px;">
            <h4 style="margin-bottom: 10px;">Calcul desfășurat:</h4>
            <p style="font-style: italic; margin: 0;">${rezultat.calculDesfasurat}</p>
          </div>
        </div>
      `;

      const opt = {
        margin: 1,
        filename: `dobanda-${tip}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().set(opt).from(element).save();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <CalculateIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Calculator Dobânzi
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="dobanda tabs"
          sx={{
            '& .MuiTab-root': {
              minWidth: 150,
              padding: '14px 28px',
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: '25px 25px 0 0',
              margin: '0 2px',
              transition: 'all 0.3s ease-in-out',
              border: '2px solid #e0e0e0',
              borderBottom: 'none',
              backgroundColor: '#fafafa',
              color: '#757575',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#424242',
                color: '#212121',
                transform: 'translateY(-2px)',
              },
              '&.Mui-selected': {
                backgroundColor: '#424242',
                borderColor: '#424242',
                color: 'white',
                fontWeight: 600,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(66, 66, 66, 0.3)',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            }
          }}
        >
          <Tab label="Dobânzi Generale" />
          <Tab label="Dobânzi Legale" />
        </Tabs>
      </Box>

      {/* Tab pentru Dobânzi Generale */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Capital inițial (RON) <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={capitalInitial}
                  onChange={(e) => setCapitalInitial(e.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Rata dobânzii (%) <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={rataDobanzi}
                  onChange={(e) => setRataDobanzi(e.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip rată <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipRata}
                    onChange={(e) => setTipRata(e.target.value)}
                  >
                    <MenuItem value="anuala">Anuală</MenuItem>
                    <MenuItem value="lunara">Lunară</MenuItem>
                    <MenuItem value="zilnica">Zilnică</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip calcul <span style={{ color: 'red' }}>*</span>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={tipCalcul}
                    onChange={(e) => setTipCalcul(e.target.value)}
                  >
                    <MenuItem value="simpla">Dobândă simplă</MenuItem>
                    <MenuItem value="compusa">Dobândă compusă</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data început <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataStart}
                  onChange={(date) => setDataStart(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data final <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataFinal}
                  onChange={(date) => setDataFinal(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={resetCalculatorSimpla}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultatSimpla && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(rezultatSimpla, 'simpla')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copiazaRezultat(rezultatSimpla, 'simpla')}
                    >
                      Copiază rezultatul
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultatSimpla && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #e9ecef',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: '#495057',
                    marginBottom: 3
                  }}
                >
                  Rezultatul Calculului Dobânzi
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Capital inițial:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatSimpla.capitalInitial} lei
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Perioada:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatSimpla.zile} zile
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ marginY: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Dobândă calculată:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc3545' }}>
                        {rezultatSimpla.dobandaCalculata} lei
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Total de plată:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                        {rezultatSimpla.totalDePlata} lei
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider sx={{ marginY: 2 }} />
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Calcul desfășurat:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontStyle: 'italic',
                      backgroundColor: '#ffffff',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #dee2e6'
                    }}
                  >
                    {rezultatSimpla.calculDesfasurat}
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab pentru Dobânzi Legale */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" color="primary">
                <strong>Informație:</strong> Dobânda legală se aplică potrivit{' '}
                <Tooltip 
                  title={
                    <Box sx={{ backgroundColor: 'white', color: 'black', padding: 2, borderRadius: 1, border: '1px solid #ccc' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: 'black' }}>
                        Codul Civil - Art. 1489 și 1535:
                      </Typography>
                      <Typography variant="body2" sx={{ marginBottom: 1, color: 'black' }}>
                        <strong>Art. 1489:</strong> Dobânda remuneratorie - când nu s-a stabilit una convențională, se aplică dobânda legală.
                      </Typography>
                      <Typography variant="body2" sx={{ marginBottom: 1, color: 'black' }}>
                        <strong>Art. 1535:</strong> Dobânda penalizatoare - în caz de întârziere la plata unei sume de bani.
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        Codul Civil face trimiterea către legea specială pentru stabilirea valorilor concrete.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        maxWidth: 500
                      }
                    },
                    arrow: {
                      sx: {
                        color: 'white',
                        '&::before': {
                          border: '1px solid #ccc'
                        }
                      }
                    }
                  }}
                >
                  <Link 
                    href={`https://ilegis.ro/oficiale/index/act/304560/data/${new Date().toISOString().split('T')[0]}#idArt1489`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    art. 1489 și art. 1535 Cod civil
                  </Link>
                </Tooltip>
                , coroborate cu{' '}
                <Tooltip 
                  title={
                    <Box sx={{ backgroundColor: 'white', color: 'black', padding: 2, borderRadius: 1, border: '1px solid #ccc' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, color: 'black' }}>
                        OG nr. 13/2011:
                      </Typography>
                      <Typography variant="body2" sx={{ marginBottom: 1, color: 'black' }}>
                        <strong>Dobânda legală remuneratorie:</strong> egală cu rata dobânzii de referință a BNR.
                      </Typography>
                      <Typography variant="body2" sx={{ marginBottom: 1, color: 'black' }}>
                        <strong>Dobânda legală penalizatoare:</strong> egală cu rata BNR + 4 puncte procentuale.
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        <strong>În raporturile dintre profesioniști:</strong> rata BNR + 8 puncte procentuale.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        maxWidth: 500
                      }
                    },
                    arrow: {
                      sx: {
                        color: 'white',
                        '&::before': {
                          border: '1px solid #ccc'
                        }
                      }
                    }
                  }}
                >
                  <Link 
                    href={`https://ilegis.ro/oficiale/index/act/289463/data/${new Date().toISOString().split('T')[0]}/dataI/${new Date().toISOString().split('T')[0]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    OG nr. 13/2011
                  </Link>
                </Tooltip>
                . Dobânda legală remuneratorie este egală cu rata dobânzii de referință a BNR, iar dobânda legală penalizatoare este egală cu rata BNR + 4 puncte procentuale (respectiv +8 puncte procentuale în raporturile dintre profesioniști).
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Capital inițial (RON) <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={capitalLegal}
                  onChange={(e) => setCapitalLegal(e.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Rata dobânzii legale (% anual) <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={rataLegala}
                  onChange={(e) => setRataLegala(e.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0, step: 'any' }}
                  helperText="Rata BNR (remuneratorie), rata BNR + 4% (penalizatoare) sau rata BNR + 8% (profesioniști)"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Tip dobândă legală
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={
                      rataLegala === '8' ? 'remuneratorie' : 
                      rataLegala === '12' ? 'penalizatoare' : 
                      rataLegala === '16' ? 'penalizatoare_profesionisti' : 
                      'custom'
                    }
                    onChange={(e) => {
                      if (e.target.value === 'remuneratorie') {
                        setRataLegala('8'); // Rata BNR aproximativă
                      } else if (e.target.value === 'penalizatoare') {
                        setRataLegala('12'); // Rata BNR + 4%
                      } else if (e.target.value === 'penalizatoare_profesionisti') {
                        setRataLegala('16'); // Rata BNR + 8%
                      }
                    }}
                  >
                    <MenuItem value="remuneratorie">Remuneratorie (rata BNR)</MenuItem>
                    <MenuItem value="penalizatoare">Penalizatoare (rata BNR + 4%)</MenuItem>
                    <MenuItem value="penalizatoare_profesionisti">Penalizatoare profesioniști (rata BNR + 8%)</MenuItem>
                    <MenuItem value="custom">Personalizată</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data început <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataStartLegal}
                  onChange={(date) => setDataStartLegal(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                  Data final <span style={{ color: 'red' }}>*</span>
                </Typography>
                <DatePicker
                  selected={dataFinalLegal}
                  onChange={(date) => setDataFinalLegal(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="zi.luna.an"
                  customInput={
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={resetCalculatorLegala}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                
                {rezultatLegala && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => exportaPDF(rezultatLegala, 'legala')}
                      sx={{ marginRight: 2 }}
                    >
                      Export PDF
                    </Button>
                    
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copiazaRezultat(rezultatLegala, 'legala')}
                    >
                      Copiază rezultatul
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>

            {rezultatLegala && (
              <Paper 
                elevation={3} 
                sx={{ 
                  marginTop: 3, 
                  padding: 3, 
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #e9ecef',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    color: '#495057',
                    marginBottom: 3
                  }}
                >
                  Rezultatul Calculului Dobânzi Legale
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Capital inițial:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatLegala.capitalLegal} lei
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Perioada:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {rezultatLegala.zile} zile
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ marginY: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Dobândă legală:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#dc3545' }}>
                        {rezultatLegala.dobandaLegala} lei
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Total de plată:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                        {rezultatLegala.totalDePlata} lei
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                <Divider sx={{ marginY: 2 }} />
                
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', marginBottom: 1 }}>
                    Calcul desfășurat:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontStyle: 'italic',
                      backgroundColor: '#ffffff',
                      padding: 2,
                      borderRadius: 1,
                      border: '1px solid #dee2e6'
                    }}
                  >
                    {rezultatLegala.calculDesfasurat}
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      </TabPanel>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Rezultatul a fost copiat în clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CalculatorDobanzi;